import { termoDaBusca } from '@/lib/texto';

import { lugarDoFormulario } from './campos';
import { ehTipoDeCadastro, type TipoDeCadastro } from './tipos';

/**
 * O estado das tres telas do modulo vive na URL (p.16).
 *
 * O motivo e de uso, nao de arquitetura: "olha os contratos arquivados" precisa
 * ser um link que se manda no WhatsApp, e "a historia deste cliente" precisa
 * ser um link que se cola num e-mail. Filtro salvo nos favoritos so funciona
 * assim.
 *
 * Valor fora do vocabulario e DESCARTADO, nunca corrigido no chute: a URL e
 * entrada do usuario, e adivinhar o que a pessoa quis dizer e como uma lista
 * passa a mostrar coisa diferente do que o filtro diz.
 */

export const SITUACOES = ['Em uso', 'Arquivado'] as const;

export type Situacao = (typeof SITUACOES)[number];

function ehSituacao(valor: unknown): valor is Situacao {
  return typeof valor === 'string' && (SITUACOES as readonly string[]).includes(valor);
}

export const PADRAO_POR_PAGINA = 10;
export const OPCOES_POR_PAGINA = [10, 25, 50, 100];

export const TIPO_PADRAO: TipoDeCadastro = 'clientes';

export interface BuscaDeCadastros {
  readonly tipo?: TipoDeCadastro;
  readonly termo?: string;
  readonly situacao?: Situacao;
  readonly pagina?: number;
  readonly porPagina?: number;
  /**
   * O formulario de pagina inteira: `novo`, ou o id da linha em edicao.
   *
   * SO existe para o cadastro que abre em pagina (`lugarDoFormulario`). Um
   * formulario que ocupa a tela inteira E uma pagina, e pagina sem endereco
   * nao volta pelo botao de voltar nem se manda por link. O que abre em janela
   * nao entra na URL: janela e estado de uma tela, nao outra tela.
   */
  readonly form?: string;
}

export function validarBuscaDeCadastros(entrada: Record<string, unknown>): BuscaDeCadastros {
  const tipo = entrada['tipo'];
  const termo = entrada['termo'];
  const situacao = entrada['situacao'];
  const pagina = Number(entrada['pagina']);
  const porPagina = Number(entrada['porPagina']);

  const termoLimpo = termoDaBusca(termo);
  const tipoValido: TipoDeCadastro = ehTipoDeCadastro(tipo) ? tipo : TIPO_PADRAO;

  return {
    ...(ehTipoDeCadastro(tipo) && tipo !== TIPO_PADRAO ? { tipo } : {}),
    ...(termoLimpo ? { termo: termoLimpo } : {}),
    ...(ehSituacao(situacao) ? { situacao } : {}),
    ...(Number.isInteger(pagina) && pagina > 1 ? { pagina } : {}),
    ...(OPCOES_POR_PAGINA.includes(porPagina) && porPagina !== PADRAO_POR_PAGINA ? { porPagina } : {}),
    ...formularioDa(entrada['form'], tipoValido),
  };
}

/**
 * O `form` da URL, conferido contra o TIPO da mesma URL.
 *
 * Um `form` com tipo que abre em janela e descartado, e nao honrado: honra-lo
 * abriria um formulario que a tela nao sabe desenhar, e o link prometeria uma
 * tela que nao existe. Descartar e a mesma regra do resto deste validador —
 * valor fora do vocabulario sai, nunca se corrige no chute.
 */
function formularioDa(valor: unknown, tipo: TipoDeCadastro): { readonly form?: string } {
  if (lugarDoFormulario(tipo) !== 'pagina') return {};
  if (typeof valor !== 'string') return {};
  const limpo = valor.trim();
  if (limpo === '') return {};
  return { form: limpo };
}

/** Monta a busca para navegar, deixando de fora tudo que ja e o padrao. */
export function montarBuscaDeCadastros(estado: {
  readonly tipo: TipoDeCadastro;
  readonly termo: string;
  readonly situacao: Situacao | undefined;
  readonly pagina: number;
  readonly porPagina: number;
  readonly form?: string | undefined;
}): BuscaDeCadastros {
  const termo = termoDaBusca(estado.termo);
  return {
    ...(estado.tipo !== TIPO_PADRAO ? { tipo: estado.tipo } : {}),
    ...(termo ? { termo } : {}),
    ...(estado.situacao ? { situacao: estado.situacao } : {}),
    ...(estado.pagina > 1 ? { pagina: estado.pagina } : {}),
    ...(estado.porPagina !== PADRAO_POR_PAGINA ? { porPagina: estado.porPagina } : {}),
    /* O mesmo conferimento da leitura, e pelo mesmo motivo: trocar para um
       tipo que abre em janela com o formulario de pagina aberto deixaria o
       `form` na URL apontando para uma tela que aquele tipo nao tem. */
    ...formularioDa(estado.form, estado.tipo),
  };
}

// ------------------------------------------------------------------ governanca

export const VISTAS = ['papeis', 'pessoas', 'alcadas'] as const;

export type Vista = (typeof VISTAS)[number];

export const NOME_DA_VISTA: Record<Vista, string> = {
  papeis: 'Papéis',
  pessoas: 'Pessoas',
  alcadas: 'Alçadas',
};

export const VISTA_PADRAO: Vista = 'papeis';

function ehVista(valor: unknown): valor is Vista {
  return typeof valor === 'string' && (VISTAS as readonly string[]).includes(valor);
}

export interface BuscaDeGovernanca {
  readonly vista?: Vista;
  readonly termo?: string;
  readonly pagina?: number;
  readonly porPagina?: number;
}

export function validarBuscaDeGovernanca(entrada: Record<string, unknown>): BuscaDeGovernanca {
  const vista = entrada['vista'];
  const termo = entrada['termo'];
  const termoLimpo = termoDaBusca(termo);

  return {
    ...(ehVista(vista) && vista !== VISTA_PADRAO ? { vista } : {}),
    ...(termoLimpo ? { termo: termoLimpo } : {}),
    ...paginacaoDa(entrada),
  };
}

/**
 * A pagina e os itens por pagina (rodape da tabela, p.18), com o mesmo
 * vocabulario de `validarBuscaDeCadastros`: o padrao fica fora da URL.
 */
function paginacaoDa(entrada: Record<string, unknown>): { readonly pagina?: number; readonly porPagina?: number } {
  const pagina = Number(entrada['pagina']);
  const porPagina = Number(entrada['porPagina']);
  return {
    ...(Number.isInteger(pagina) && pagina > 1 ? { pagina } : {}),
    ...(OPCOES_POR_PAGINA.includes(porPagina) && porPagina !== PADRAO_POR_PAGINA ? { porPagina } : {}),
  };
}

// ---------------------------------------------------------------------- trilha

export const ACOES_DA_TRILHA = ['insert', 'update', 'delete', 'ler'] as const;

export type AcaoDaTrilha = (typeof ACOES_DA_TRILHA)[number];

function ehAcao(valor: unknown): valor is AcaoDaTrilha {
  return typeof valor === 'string' && (ACOES_DA_TRILHA as readonly string[]).includes(valor);
}

export interface BuscaDaTrilha {
  readonly recurso?: string;
  readonly recursoId?: string;
  readonly acao?: AcaoDaTrilha;
  readonly pagina?: number;
  readonly porPagina?: number;
}

export function validarBuscaDaTrilha(entrada: Record<string, unknown>): BuscaDaTrilha {
  const recurso = entrada['recurso'];
  const recursoId = entrada['recursoId'];
  const acao = entrada['acao'];

  /* O recurso e o nome da tabela como o trigger o escreve. Qualquer outra coisa
     nao acharia linha nenhuma e faria a tela parecer vazia por defeito. */
  const recursoLimpo = typeof recurso === 'string' && /^[a-z_]+\.[a-z_]+$/.test(recurso) ? recurso : '';
  const idLimpo = typeof recursoId === 'string' ? recursoId.trim() : '';

  return {
    ...(recursoLimpo ? { recurso: recursoLimpo } : {}),
    ...(idLimpo ? { recursoId: idLimpo } : {}),
    ...(ehAcao(acao) ? { acao } : {}),
    ...paginacaoDa(entrada),
  };
}
